import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { cx } from '@emotion/css';
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';

import { Typography } from '@common/styles';

export type TextAreaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
  changeEvent?: (value: string) => void;
  onSave?: () => Promise<boolean>;
  labelCss?: SerializedStyles;
};

function textLengthReducer(state: any, action: any) {
  const maxLength = action.maxLength;
  if (maxLength) {
    const textarea = action.textareaRef.current;
    if (textarea) {
      const overLength = textarea.value.length - maxLength;
      if (0 < overLength) {
        if (overLength === 1) {
          const selection = textarea.selectionStart - 1;
          textarea.value = state.prevValue;
          textarea.setSelectionRange(selection, selection);
        } else {
          textarea.value = textarea.value.slice(0, maxLength);
        }
      }
      return {
        prevValue: textarea.value,
        textLength: textarea.value.length,
      };
    }
  }
  return {};
}

export const TextArea = ({
  children,
  className,
  maxLength,
  changeEvent,
  onSave,
  onFocus,
  onClick,
  labelCss: label_custom_css,
  ...otherProps
}: TextAreaProps) => {
  const textareaRef: React.RefObject<HTMLTextAreaElement> = useRef(null);
  const [textLengthState, textLengthDispatch] = useReducer(textLengthReducer, {
    prevValue: '',
    textLength: 0,
  });

  const textLengthStatus = `${textLengthState.textLength}/${maxLength}`;
  const hasMaxLength = !!maxLength;

  const onFocusTextArea: React.FocusEventHandler<HTMLTextAreaElement> = useCallback(
    (event) => {
      onFocus?.(event);
    },
    [onFocus],
  );

  const onClickTextArea: React.MouseEventHandler<HTMLTextAreaElement> = useCallback(
    (event) => {
      onClick?.(event);
    },
    [onClick],
  );

  const textarea = textareaRef.current;

  const onInput = () =>
    textLengthDispatch({
      maxLength,
      textareaRef,
    });

  const onChange = () => {
    if (changeEvent) {
      changeEvent(textarea ? (maxLength ? textarea.value.slice(0, maxLength) : textarea.value) : '');
    }
  };
  useEffect(onInput, [maxLength, otherProps.value]);

  return (
    <Label
      maxLength={maxLength}
      showBottom={hasMaxLength}
      css={css`
        ${label_custom_css}
      `}
    >
      <Textarea
        ref={textareaRef}
        className={cx(Typography.BODY_15_MEDIUM, className)}
        onInput={onInput}
        onChange={onChange}
        showBottom={hasMaxLength}
        onFocus={onFocusTextArea}
        onClick={onClickTextArea}
        {...otherProps}
      >
        {children}
      </Textarea>
      {hasMaxLength && (
        <BottomLayout hasMaxLength={hasMaxLength}>
          <Counter variant={'Caption_12_SemiBold'} color={'textTertiary'}>
            {textLengthStatus}
          </Counter>
        </BottomLayout>
      )}
    </Label>
  );
};

const Label = styled.label<{ maxLength: number | undefined; showBottom: boolean }>`
  display: flex;
  flex-direction: column;
  margin: 8px 0;
  padding: 12px;
  width: 100%;
  height: ${({ showBottom }) => (!showBottom ? '88px' : '122px')};
  border-radius: 8px;
  border: 1px solid ${vars.color.textTertiary};
`;

const Textarea = styled.textarea<{ showBottom: boolean }>`
  box-sizing: border-box;
  height: 100%;
  line-height: 1.35;
  resize: none;
  color: ${vars.color.textPrimary};
  background: transparent;
  &::placeholder {
    color: ${vars.color.textTertiary};
  }
`;

const Counter = styled(Text)`
  line-height: 1.235;
`;

const BottomLayout = styled.div<{ hasMaxLength: boolean }>`
  display: flex;
  justify-content: end;
  margin-top: 8px;
  padding-top: 8px;
  border-top: solid 1px ${vars.color.textTertiary};
`;
